import {
    ProfessionalOptionDTO,
    ServiceOptionDTO,
    ServiceStatisticsOptionsDTO,
    ServiceStatisticsTemplateType,
    SpecialityOptionDTO,
} from '../../dto/ServiceStatisticsDTO';

/**
 * Filtros encadenados de Estadísticas de atención: cada select muestra solo lo coherente con lo que ya se eligió en los
 * otros, con la única respuesta de `/options` (sin pedidos extra). Son funciones puras: la pantalla las llama con las
 * opciones y la selección actual.
 *
 * Reglas (`0` = «Todos» en todos los campos):
 *  - Tipo (consultorios / servicios): siempre las tres opciones, no se acota.
 *  - Especialidad: las que figuran en agendas del tipo elegido, las del profesional elegido y las del servicio o agenda
 *    elegido.
 *  - Profesional: no aplica a servicios (los servicios no son de un profesional). Si Tipo = Servicios, o se eligió un
 *    servicio y todavía no hay profesional, el campo no aplica y no se muestra (`filterVisibility`). Si aplica, solo los
 *    que tienen alguna agenda de consultorio (o ninguna registrada: solo evolucionan) y, si hay especialidad o agenda de
 *    consultorio elegida, los que la tienen.
 *  - Servicio o agenda: los del tipo y la especialidad elegidos. Con un profesional elegido, sus agendas de consultorio
 *    más todos los servicios: se elige un servicio para ver las atenciones de ese profesional en él.
 *
 * Un dato que el backend todavía no manda (`undefined`) no acota; una lista vacía sí (la especialidad sin agendas de ese
 * tipo, el profesional sin agendas).
 *
 * Casos para revisar a mano (o con un runner, cuando el repo tenga uno):
 *  1. Tipo = Servicios → `professionalApplies` es false y `specialities` solo trae las de `templateTypes` con 2.
 *  2. Profesional P (consultorios 10 y 11) → `services` trae 10, 11 y todos los de tipo 2, ningún otro consultorio.
 *  3. Elegir el servicio 7 (tipo 2) con Tipo = Todos y sin profesional → `reconcileSelection` fija Tipo = Servicios.
 *  4. Elegir Tipo = Servicios con un profesional elegido → `reconcileSelection` lo limpia.
 *  5. Elegir la especialidad X con el servicio 10 y el profesional P elegidos y 10 sin X → se limpia el servicio.
 */

/** Tipo de agenda de los filtros: 0 = todos, 1 consultorios, 2 servicios. */
export type TemplateTypeSelection = 0 | ServiceStatisticsTemplateType;

const ALL = 0;
const ROOM_TYPE: ServiceStatisticsTemplateType = 1;
const SERVICE_TYPE: ServiceStatisticsTemplateType = 2;

/** Los cuatro filtros que se acomodan entre sí. */
export interface FilterSelection {
    templateType: TemplateTypeSelection;
    specialityId: number;
    professionalId: number;
    serviceId: number;
}

export type FilterField = keyof FilterSelection;

/** Lo coherente con la selección, para llenar cada select (sin la opción «Todos»). */
export interface FilterChoices {
    specialities: SpecialityOptionDTO[];
    professionals: ProfessionalOptionDTO[];
    services: ServiceOptionDTO[];
    /** false = el filtro de profesional no aplica (servicios): no se muestra. */
    professionalApplies: boolean;
}

/** `true` si el dato no se conoce (el backend no lo manda) o la lista lo incluye. */
const allows = (list: readonly number[] | undefined, id: number): boolean => list === undefined || list.includes(id);

/** Lo mismo para tipos de agenda. */
const allowsType = (types: readonly number[] | undefined, type: number): boolean => types === undefined || types.includes(type);

/** El profesional puede usarse como filtro: no hay Tipo = Servicios y, si el servicio elegido es un servicio, ya hay profesional. */
function professionalApplies(selection: FilterSelection, service: ServiceOptionDTO | undefined): boolean {
    if (selection.templateType === SERVICE_TYPE) return false;
    return selection.professionalId !== ALL || service?.templateType !== SERVICE_TYPE;
}

export function filterOptions(options: ServiceStatisticsOptionsDTO, selection: FilterSelection): FilterChoices {
    const service = options.services.find(item => item.id === selection.serviceId);
    const applies = professionalApplies(selection, service);
    // El profesional elegido, solo si el filtro aplica
    const professional = applies ? options.professionals.find(item => item.id === selection.professionalId) : undefined;
    const roomService = service?.templateType === ROOM_TYPE ? service : undefined;

    const specialities = options.specialities.filter(item =>
        (!selection.templateType || allowsType(item.templateTypes, selection.templateType))
        && (!professional || allows(professional.specialityIds, item.id))
        // Un servicio no acota la especialidad de un profesional: la guardia no es de una especialidad de él
        && (!service || (professional !== undefined && service.templateType === SERVICE_TYPE) || allows(service.specialityIds, item.id)));

    const professionals = options.professionals.filter(item =>
        // Sin agendas registradas el profesional solo evoluciona: se lo deja elegir igual
        (item.templateTypes === undefined || item.templateTypes.length === 0 || item.templateTypes.includes(ROOM_TYPE))
        && (!selection.specialityId || allows(item.specialityIds, selection.specialityId))
        && (!roomService || allows(item.serviceIds, roomService.id)));

    const services = options.services.filter(item => {
        if (selection.templateType && item.templateType !== selection.templateType) return false;
        const ofProfessional = professional !== undefined && item.templateType === SERVICE_TYPE;
        if (selection.specialityId && !ofProfessional && !allows(item.specialityIds, selection.specialityId)) return false;
        // De un profesional son sus agendas de consultorio; los servicios no son de nadie y quedan todos
        if (professional && item.templateType === ROOM_TYPE && !allows(professional.serviceIds, item.id)) return false;
        return true;
    });

    return { specialities, professionals, services, professionalApplies: applies };
}

/** Qué selectores de filtro se muestran: los que no aportan en el contexto actual desaparecen (no se deshabilitan). */
export type FilterVisibility = Record<FilterField, boolean>;

/**
 * Qué filtros mostrar. Un filtro se oculta cuando no aporta:
 *  - Tipo: en «mis agendas» no existe; con un servicio o agenda elegido el tipo ya está implícito (el chip sigue mostrando el
 *    valor aplicado); y si el sitio solo tiene un tipo de agenda, no hay qué elegir.
 *  - Profesional: no existe en «mis agendas» ni sin la acción de ver a todos, no aplica a servicios (`professionalApplies`) y
 *    se oculta si no hay profesionales entre los que elegir.
 *  - Especialidad y Servicio o agenda: se ocultan si hay una sola opción (aparte de «Todos»).
 * Un filtro que ya tiene un valor elegido se sigue mostrando, para poder verlo y quitarlo. Mientras las opciones no llegaron
 * (`options` es null) los selectores ocupan su lugar.
 */
export function filterVisibility(options: ServiceStatisticsOptionsDTO | null, choices: FilterChoices | null,
                                 selection: FilterSelection, mine: boolean): FilterVisibility {
    if (!options || !choices) {
        return { templateType: !mine, specialityId: true, professionalId: !mine, serviceId: true };
    }
    const hasChoice = (count: number, field: FilterField) => count > 1 || selection[field] !== ALL;
    const types = new Set(options.services.map(item => item.templateType));
    return {
        templateType: !mine && selection.serviceId === ALL && (types.size > 1 || selection.templateType !== ALL),
        specialityId: hasChoice(choices.specialities.length, 'specialityId'),
        professionalId: !mine && options.access.canViewAll && choices.professionalApplies
            && hasChoice(choices.professionals.length, 'professionalId'),
        serviceId: hasChoice(choices.services.length, 'serviceId'),
    };
}

/**
 * Deja la selección coherente después de que el usuario cambió `changed` (`null` = no cambió ninguno, solo se revisa):
 *  - Elegir un servicio o agenda con Tipo = Todos fija el Tipo al de ese servicio (no en «mis agendas», que no tiene Tipo, ni
 *    con un profesional elegido: con él se elige un servicio justamente para ver sus atenciones ahí).
 *  - Elegir Tipo = Servicios limpia el profesional.
 *  - Lo que deja de ser coherente vuelve a «Todos», de a un campo por vez (servicio, especialidad, profesional) y volviendo a
 *    calcular las listas, así se limpia lo mínimo. El campo que acaba de cambiar nunca se limpia.
 * Devuelve la misma selección si no hubo nada que acomodar (no genera otro render).
 */
export function reconcileSelection<T extends FilterSelection>(
    options: ServiceStatisticsOptionsDTO, selection: T, changed: FilterField | null, mine = false): T {
    let next: T = selection;
    const patch = (fields: Partial<FilterSelection>) => { next = { ...next, ...fields }; };

    if (changed === 'serviceId' && !mine && next.serviceId !== ALL && next.templateType === ALL && next.professionalId === ALL) {
        const service = options.services.find(item => item.id === next.serviceId);
        if (service) patch({ templateType: service.templateType });
    }
    if (changed === 'templateType' && next.templateType === SERVICE_TYPE && next.professionalId !== ALL) {
        patch({ professionalId: ALL });
    }

    // Cada pasada puede limpiar un campo y ensanchar las listas de los otros: se repite hasta que no cambia nada (como mucho 3)
    for (let pass = 0; pass < 3; pass += 1) {
        const choices = filterOptions(options, next);
        const dropService = changed !== 'serviceId' && next.serviceId !== ALL
            && !choices.services.some(item => item.id === next.serviceId);
        if (dropService) { patch({ serviceId: ALL }); continue; }
        const dropSpeciality = changed !== 'specialityId' && next.specialityId !== ALL
            && !choices.specialities.some(item => item.id === next.specialityId);
        if (dropSpeciality) { patch({ specialityId: ALL }); continue; }
        const dropProfessional = changed !== 'professionalId' && next.professionalId !== ALL
            && (!choices.professionalApplies || !choices.professionals.some(item => item.id === next.professionalId));
        if (dropProfessional) { patch({ professionalId: ALL }); continue; }
        break;
    }
    return next;
}
